// Phase 8.6 follow-up — employee feature write helpers (create + update).
//
// The personnel write path: POST /v1/employees (create — lands at pre_hire) +
// PATCH /v1/employees/:id (partial update). Explicit adapter signatures retain
// the local DTO boundary across the generated paths' request/response metadata.
// Server contract:
// services/platform-graph/src/routes/employees.ts CreateEmployeeSchema /
// PatchEmployeeSchema.
//
// Idempotency wave (Batch 5): POST /v1/employees is spec `idempotency:
// required` (18-hris.yaml:690) — a double-submit / network retry must not
// register a second pre_hire row. The form host owns the key so it can replay
// an ambiguous response with the exact same body.
//
// Idempotency wave (Batch 8a): PATCH /v1/employees/:id is ALSO spec
// `idempotency: required` (18-hris.yaml:698) — the host owns its retry key
// here as well.

import { type EmployeeLifecycleState } from "@novemai/hris-sdk";

import { platformGraph } from "@/api/platform-graph";
import {
  unwrapEnvelope,
  type EnvelopedFetchResult,
} from "@/api/errors";
import { type KeysetPage } from "@/api/keyset";

/** The employee row the API returns (the documented EmployeeResponseRow). */
export interface EmployeeRow {
  readonly employee_id: string;
  readonly employee_number: string | null;
  readonly legal_first_name: string;
  readonly legal_last_name: string;
  readonly primary_work_email: string;
  readonly job_title: string | null;
  readonly lifecycle_state: EmployeeLifecycleState;
  readonly employment_type: string | null;
  readonly start_date: string | null;
  /** Kıdem başlangıcı — çıkış/kıdem ekranı bunu salt okunur gösterir. */
  readonly seniority_date: string | null;
  readonly manager_id: string | null;
  readonly legal_entity_id: string | null;
  readonly department_id: string | null;
  readonly manager_name: string | null;
  readonly department_name: string | null;
  /** ADR-012 org dimension values, keyed by dimension slug (SEM-7205). */
  readonly org_dimension_values: Readonly<Record<string, string>> | null;
}

/** The create payload — the required fields + the optional v1 fields. Ünvan
 * MC-19a ile zorunlu: işe alım birincil istihdam kaydını açıyor ve o kaydın
 * ünvanı boş olamaz. */
export interface CreateEmployeeInput {
  readonly legal_first_name: string;
  readonly legal_last_name: string;
  readonly primary_work_email: string;
  /** Date-only ISO string ("2026-06-16"); the backend coerces to a Date. */
  readonly start_date: string;
  readonly job_title: string;
  readonly employment_type?: string;
  readonly manager_id?: string;
  /** Yalnız atanır: uç `department_id`'yi kabul eder ama null ile temizlemez. */
  readonly department_id?: string;
  readonly legal_entity_id?: string;
}

/**
 * PATCH is all-partial — any subset of the create fields, EKSİ şirket. Yama ucu
 * `legal_entity_id`'yi reddediyor (MC-19b / SEM-6781): şirket değiştirmek bir
 * transfer geçişidir, `POST /v1/employees/:id/transitions`. Alanı burada
 * bırakmak, tipin çağırana yalan söylemesi olurdu.
 */
// `org_dimension_values` yalnız yamada: boyut kataloğu kiracı ayarıdır ve
// yaratma ucu onu kabul etmiyor (SEM-7205).
export type UpdateEmployeeInput = Partial<
  Omit<CreateEmployeeInput, "legal_entity_id">
> & {
  readonly org_dimension_values?: Readonly<Record<string, string>>;
};

// ---- typed-cast call signatures for the consumed wire contracts ---------

type EmployeesGet = (
  path: "/v1/employees",
  init: {
    params: {
      query: {
        cursor?: string;
        q?: string;
        limit?: number;
        lifecycle_state?: string;
        manager_id?: string;
      };
    };
  },
) => Promise<EnvelopedFetchResult<KeysetPage<EmployeeRow>>>;

/**
 * Kişi sayıları, satır penceresinden değil sunucudan: liste ilk sayfada (100)
 * kesilir ve bir pencereyi sayan sayaç "100+" gibi bir yalan söyler. Uç, listeyle
 * AYNI kapsam daraltmasını uygular, yani sayaç listenin gösterebileceğinden
 * fazlasını saymaz.
 */
export interface EmployeeSummary {
  readonly total: number;
  /** lifecycle_state → kişi sayısı; sıfır olan durum anahtarı yoktur. */
  readonly by_lifecycle: Readonly<Record<string, number>>;
  /** Departmanı olan kişilerin departmanları; departmansızlar burada yoktur. */
  readonly departments: readonly {
    readonly department_id: string;
    readonly department_name: string | null;
    readonly count: number;
  }[];
}

type EmployeeSummaryGet = (
  path: "/v1/employees/summary",
) => Promise<EnvelopedFetchResult<EmployeeSummary>>;

export async function fetchEmployeeSummary(): Promise<EmployeeSummary> {
  const get = platformGraph.GET as unknown as EmployeeSummaryGet;
  return unwrapEnvelope(get("/v1/employees/summary"));
}

type GetEmployee = (
  path: "/v1/employees/{id}",
  init: { params: { path: { id: string } } },
) => Promise<EnvelopedFetchResult<{ employee: EmployeeRow }>>;

type PostEmployee = (
  path: "/v1/employees",
  init: { body: CreateEmployeeInput; headers: Record<string, string> },
) => Promise<EnvelopedFetchResult<{ employee: EmployeeRow }>>;

type PatchEmployee = (
  path: "/v1/employees/{id}",
  init: {
    params: { path: { id: string } };
    body: UpdateEmployeeInput;
    headers: Record<string, string>;
  },
) => Promise<EnvelopedFetchResult<{ employee: EmployeeRow }>>;

// ---- mutation + query functions -------------------------------------------

export async function fetchEmployee(id: string): Promise<EmployeeRow> {
  const get = platformGraph.GET as unknown as GetEmployee;
  const result = await unwrapEnvelope(
    get("/v1/employees/{id}", { params: { path: { id } } }),
  );
  return result.employee;
}

const SEARCH_PAGE_SIZE = 20;

/**
 * Server-side person search — the picker's option source (SEM-8105).
 *
 * `fetchEmployeesFirstPage` below is the bounded-option-set variant, and on a
 * tenant past one page it silently hides everyone after row 100. Where the
 * picker must be able to reach ANY person, ask the server to narrow instead of
 * paging the client through the whole directory. An empty `q` returns the same
 * first page, so a picker can open before anything is typed.
 */
export async function searchEmployees(q: string): Promise<KeysetPage<EmployeeRow>> {
  const get = platformGraph.GET as unknown as EmployeesGet;
  const trimmed = q.trim();
  return unwrapEnvelope(
    get("/v1/employees", {
      params: { query: { limit: SEARCH_PAGE_SIZE, ...(trimmed !== "" && { q: trimmed }) } },
    }),
  );
}

/** First page of the employees list — the ready option-source for the manager
 * picker (a bounded option set; no pagination needed for the picker). */
export async function fetchEmployeesFirstPage(): Promise<KeysetPage<EmployeeRow>> {
  const get = platformGraph.GET as unknown as EmployeesGet;
  return unwrapEnvelope(get("/v1/employees", { params: { query: {} } }));
}

/**
 * First page of the people in ONE lifecycle state, filtered by the server.
 *
 * The unfiltered first page is keyset-ordered oldest-first, so on a tenant past
 * one page it never reaches the newest rows — and the newest rows are exactly
 * the people a `pre_hire`/`onboarding` screen exists for. A picker that must
 * offer a fresh hire asks for that state directly instead of hoping the hire
 * falls inside the first hundred.
 */
export async function fetchEmployeesByLifecycle(
  state: EmployeeLifecycleState,
): Promise<KeysetPage<EmployeeRow>> {
  const get = platformGraph.GET as unknown as EmployeesGet;
  return unwrapEnvelope(get("/v1/employees", { params: { query: { lifecycle_state: state } } }));
}

/**
 * One keyset page of the people whose solid-line manager is `managerId` — the
 * direct reports. The server still applies the caller's own row scope, so a
 * caller who may not read that subtree gets an empty page, never a wider one.
 */
export async function fetchDirectReportsPage(
  managerId: string,
  cursor: string | null,
): Promise<KeysetPage<EmployeeRow>> {
  const get = platformGraph.GET as unknown as EmployeesGet;
  return unwrapEnvelope(
    get("/v1/employees", {
      params: { query: { manager_id: managerId, ...(cursor !== null && { cursor }) } },
    }),
  );
}

export async function createEmployee(
  input: CreateEmployeeInput,
  idempotencyKey: string,
): Promise<EmployeeRow> {
  const post = platformGraph.POST as unknown as PostEmployee;
  const result = await unwrapEnvelope(
    post("/v1/employees", { body: input, headers: { "x-idempotency-key": idempotencyKey } }),
  );
  return result.employee;
}

export async function updateEmployee(
  id: string,
  partial: UpdateEmployeeInput,
  idempotencyKey: string,
): Promise<EmployeeRow> {
  const patch = platformGraph.PATCH as unknown as PatchEmployee;
  const result = await unwrapEnvelope(
    patch("/v1/employees/{id}", {
      params: { path: { id } },
      body: partial,
      headers: { "x-idempotency-key": idempotencyKey },
    }),
  );
  return result.employee;
}
